import { DYNAMIC_VALUE_NOTE } from "@/constants/warmUpMergeTags";

const MAX_DISPLAY_LEN = 120;

/** Example values for per-lead chat tags (preview only). */
export const EXAMPLE_CHAT_VALUES = {
  "chat.id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "chat.lead_name": "Sarah Mitchell",
  "chat.lead_contact": "+61412345678",
  "chat.platform": "whatsapp",
  "chat.ai_active": "true",
  "chat.ghl_opportunity_id": "opp_8f3k2m9x1",
  "chat.timestamps.created_at": "2026-05-10T14:22:00Z",
  "chat.timestamps.updated_at": "2026-05-18T09:15:00Z",
  "chat.metrics.pipeline_stage": "meeting_booked",
  "chat.metrics.lead_crm": "TBD",
  "chat.metrics.ai_response_count": "12",

  "chat.client_data.client_name": "Sarah Mitchell",
  "chat.client_data.property_address": "42 Ocean View Dr, Wollongong NSW 2500",
  "chat.client_data.property_state": "NSW",
  "chat.client_data.property_country": "Australia",
  "chat.client_data.property_zip_code": "2500",
  "chat.client_data.property_geo.lat": "-34.4244",
  "chat.client_data.property_geo.lng": "150.8931",
  "chat.client_data.distance_to_base_km": "18",
  "chat.client_data.contact_phone": "+61412345678",
  "chat.client_data.contact_email": "sarah.mitchell@example.com",
  "chat.client_data.issue_summary": "Active leak near rear patio after recent storms",
  "chat.client_data.quote_reference_number": "Q-2026-0412",
  "chat.client_data.square_footage": "2200",
  "chat.client_data.original_job_reference": "JOB-2019-088",

  "chat.scheduling.selected_appointment_type": "free_quote_inspection",
  "chat.scheduling.confirmed_slot": "2026-05-22T09:00:00+10:00",
  "chat.scheduling.requested_timeframe": "Thursday morning",
  "chat.scheduling.calculated_price": "$0",
  "chat.scheduling.booking_reference_id": "cal_evt_7x9k2",
  "chat.scheduling.booking_status": "confirmed",
  "chat.scheduling.callback_preferred": "false",
  "chat.scheduling.callback_time": "Tomorrow after 2pm",
  "chat.scheduling.days_until_meeting": "3",
  "chat.scheduling.meeting_date": "Thursday, 22 May 2026",
  "chat.scheduling.meeting_time": "9:00 AM",

  "chat.qualification.property_address": "42 Ocean View Dr, Wollongong NSW 2500",
  "chat.qualification.ownership_status": "Owner",
  "chat.qualification.property_type": "House",
  "chat.qualification.storey_count": "Double storey",
  "chat.qualification.roof_type": "Concrete tile",
  "chat.qualification.roof_age": "18 years",
  "chat.qualification.issue_details": "Leak in valley iron after heavy rain",
  "chat.qualification.timeline_urgency": "Active leak now",
  "chat.qualification.previous_quotes": "No",
  "chat.qualification.insurance_status": "No claim",
  "chat.qualification.has_photos": "Yes",
  "chat.qualification.preferred_contact_method": "SMS",
  "chat.qualification.preferred_callback_time": "Morning, before 11am",
  "chat.qualification.lead_source": "Google search",
  "chat.qualification.budget_range": "$8,000 – $12,000",
  "chat.qualification.current_crm": "GoHighLevel",

  "chat.validation.qualification_complete": "true",
  "chat.validation.is_disqualified": "false",
  "chat.validation.disqualification_reason": "",
  "chat.validation.location_approved": "true",
  "chat.validation.weather_approved": "true",
  "chat.validation.environmental_context.forecasts[0].will_rain": "true",
  "chat.validation.environmental_context.forecasts[0].rain_mm": "12.5",
  "chat.validation.environmental_context.forecasts[0].time_checked": "2026-05-18T06:00:00+10:00",
  "chat.validation.environmental_context.sunrise": "6:42 AM",
  "chat.validation.environmental_context.sunset": "5:08 PM",

  "chat.orchestrator.handoff_required": "false",
  "chat.orchestrator.handoff_reason": "",
  "chat.orchestrator.is_known_user": "false",
  "chat.orchestrator.human_request_count": "0",
};

export function parseTagPath(tag) {
  if (!tag || typeof tag !== "string") return "";
  const t = tag.trim();
  if (t.startsWith("{") && t.endsWith("}")) return t.slice(1, -1);
  return t;
}

/**
 * Walk obj by path with dot segments and [index] brackets.
 * e.g. knowledge_base.handoff_settings.roles[0].phone
 */
export function getNestedValue(obj, path) {
  if (!path || obj == null) return undefined;

  const segments = [];
  const re = /([^.\[\]]+)|\[(\d+)\]/g;
  let m;
  while ((m = re.exec(path)) !== null) {
    if (m[1] !== undefined) segments.push(m[1]);
    else if (m[2] !== undefined) segments.push(Number(m[2]));
  }

  let cur = obj;
  for (const seg of segments) {
    if (cur == null) return undefined;
    cur = cur[seg];
  }
  return cur;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** ISO date or date-time; wall-clock hours/minutes are taken from the string itself. */
const ISO_DATE_TIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

export function looksLikeIsoDateTime(value) {
  if (typeof value !== "string") return false;
  return ISO_DATE_TIME_RE.test(value.trim());
}

/**
 * Format ISO strings for tag preview, e.g. 2026-05-22T09:00:00+10:00 → 22 May 2026, 9:00 AM.
 * Returns null if the value is not ISO-shaped (caller keeps original text).
 */
export function formatReadableDateTime(value) {
  if (typeof value !== "string") return null;
  const m = value.trim().match(ISO_DATE_TIME_RE);
  if (!m) return null;

  const year = m[1];
  const monthIndex = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (monthIndex < 1 || monthIndex > 12 || Number.isNaN(day)) return null;

  const datePart = `${day} ${MONTH_NAMES[monthIndex - 1]} ${year}`;
  const hourStr = m[4];
  const minuteStr = m[5];

  if (hourStr === undefined || minuteStr === undefined) {
    return datePart;
  }

  const hour24 = parseInt(hourStr, 10);
  const minute = minuteStr.padStart(2, "0");
  if (Number.isNaN(hour24)) return datePart;

  const ampm = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  return `${datePart}, ${hour12}:${minute} ${ampm}`;
}

export function formatPreviewValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return null;
    const readable = formatReadableDateTime(s);
    const display = readable ?? s;
    if (display.length <= MAX_DISPLAY_LEN) return display;
    return `${display.slice(0, MAX_DISPLAY_LEN)}…`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const preview = value
      .slice(0, 3)
      .map((v) => (typeof v === "object" ? "…" : String(v)))
      .join(", ");
    const str = `[${preview}${value.length > 3 ? ", …" : ""}]`;
    return str.length <= MAX_DISPLAY_LEN ? str : `${str.slice(0, MAX_DISPLAY_LEN)}…`;
  }
  if (typeof value === "object") {
    const keys = Object.keys(value);
    if (keys.length === 0) return null;
    if (keys.length <= 4 && keys.every((k) => typeof value[k] !== "object")) {
      const pairs = keys.slice(0, 4).map((k) => `${k}: ${value[k]}`);
      const str = pairs.join(", ");
      return str.length <= MAX_DISPLAY_LEN ? str : `${str.slice(0, MAX_DISPLAY_LEN)}…`;
    }
    return "Configured";
  }
  return String(value);
}

function defaultChatExample(path, tagEntry) {
  if (path.startsWith("chat.qualification.")) {
    const desc = tagEntry?.description || "";
    const answerPrefix = "Answer to: ";
    if (desc.startsWith(answerPrefix)) {
      const hint = desc.slice(answerPrefix.length).trim();
      if (hint.length > 0 && hint.length <= 80) return hint;
    }
    return "Customer answer";
  }
  if (path.includes("qualification")) return "Customer answer";
  if (path.includes("scheduling")) return "Varies per booking";
  if (path.includes("validation") || path.includes("environmental")) return "Varies per property";
  return "Varies per lead";
}

export function resolveTagPreview(tagEntry, botSettings, options = {}) {
  const { settingsLoading = false } = options;
  const path = parseTagPath(tagEntry?.tag);
  const valueSource = tagEntry?.valueSource ?? (path.startsWith("bot_settings.") ? "bot_settings" : "chat");

  if (valueSource === "bot_settings") {
    if (settingsLoading) {
      return {
        kind: "current",
        display: null,
        isEmpty: true,
        loading: true,
      };
    }
    const innerPath = path.replace(/^bot_settings\./, "");
    const raw = botSettings ? getNestedValue(botSettings, innerPath) : undefined;
    const display = formatPreviewValue(raw);
    const isEmpty =
      raw === null ||
      raw === undefined ||
      (typeof raw === "string" && !raw.trim()) ||
      display === null;
    return {
      kind: "current",
      display,
      isEmpty,
      loading: false,
    };
  }

  const rawExample = EXAMPLE_CHAT_VALUES[path] ?? defaultChatExample(path, tagEntry);
  return {
    kind: "example",
    display: formatPreviewValue(rawExample) ?? "Varies per lead",
    isDynamic: true,
    dynamicNote: DYNAMIC_VALUE_NOTE,
  };
}

/** Searchable text for a tag row (tag + description + preview). */
export function tagSearchBlob(tagEntry, preview) {
  const parts = [tagEntry?.tag, tagEntry?.description];
  if (preview?.display) parts.push(preview.display);
  if (preview?.dynamicNote) parts.push(preview.dynamicNote);
  return parts.filter(Boolean).join(" ").toLowerCase();
}
